<script setup lang="ts">
import { formToObject, invokeMultipartEncoding } from '../js-utils/_shared/form-utils';
import { onMounted, ref, type ComponentInternalInstance } from 'vue';

// declare required types / interfaces
interface ItemInfo {
    nombre: string;
    telefono: string;
    mediosContacto: { tipo: string; valor: string; orden: number; }[];
    direccion: string;
    latitud: string;
    longitud: string;
    especialidadId: number;
    especialidadNombre: string;
    localidadNombre: string;
    informacionAdicional: string;
}

// declare the component props
const props = defineProps<{
    apiUrl: String;
    isMultipartEncoding: Boolean;
}>();

// declare the reactive properties
const rows = ref<ItemInfo[]>([]);
const filterRows = ref<ItemInfo[]>([]);
const totalCount = ref(0);
const reportLink = ref('');

// declare the element references
// const commentsRoot = ref<HTMLElement | null>(null);

// declare references to external MAIN BLOCK elements
let formElement: HTMLFormElement = null!; // the form to be submitted
let processingHide: HTMLElement = null!; // block to hide while processing (button)
let processingShow: HTMLElement = null!; // block to show while processing (message)
let successHide: HTMLElement = null!; // block to hide after API success
let successShow: HTMLElement = null!; // block to show after API success
let failurePHolder: HTMLElement = null!; // placeholder to show failure message
let noResultsShow: HTMLElement = null!; // message to show when no results for this search
let searchInput: HTMLInputElement = null!; // search textbox

// declare references to external FILTERS elements
let zonaTexts: HTMLElement | null = null; // hidden span holding localidades texts
let localTexts: HTMLElement | null = null; // hidden span holding localidades texts
let especTexts: HTMLElement | null = null; // hidden span holding especialidades texts

// declare references to external RESULTS elements
let especLabel: HTMLElement = null!; // label to show selected especialidades
let zonaLocalLabel: HTMLElement = null!; // label to show selected zona/localidades
let resultsCount: HTMLElement = null!; // placeholder for results count
let totalCountElement: HTMLElement = null!; // placeholder for total count
let partialResults: HTMLElement = null!; // placeholder for message about partial results
let reportLinkElement: HTMLAnchorElement = null!; // placeholder for message about partial results
let map: any = null!;
let mapElement: HTMLElement = null!;

// other properties

// implement tasks on coponent mount
onMounted(async (callback: () => void, target?: ComponentInternalInstance | null) => {
    const container = document.querySelector('.js-cartilla-search-container')! as HTMLElement;

    // get references to MAIN BLOCK elements
    successHide = container.querySelector('.js-success-hide')!;
    successShow = container.querySelector('.js-success-show')!;
    searchInput = container.querySelector('.js-search-input')!;
    formElement = successHide.querySelector('form')!;
    processingHide = successHide.querySelector('.js-processing-hide')!;
    processingShow = successHide.querySelector('.js-processing-show')!;
    failurePHolder = successHide.querySelector('.js-failure-pholder')!;
    noResultsShow = successHide.querySelector('.js-no-results-show')!;

    // get references to FILTERS elements
    zonaTexts = formElement.querySelector('.js-zona-texts');
    localTexts = formElement.querySelector('.js-localidades-texts');
    especTexts = formElement.querySelector('.js-especialidades-texts');

    // get references to RESULTS elements
    especLabel = successShow.querySelector('.js-espec-label')!;
    zonaLocalLabel = successShow.querySelector('.js-zona-local-label')!;
    resultsCount = successShow.querySelector('.js-result-count')!;
    totalCountElement = successShow.querySelector('.js-total-count')!;
    partialResults = successShow.querySelector('.js-partial-results')!;
    reportLinkElement = successShow.querySelector('.js-report-link')!;
    mapElement = successShow.querySelector('.js-map')!;

    // close results, reopen search on back link
    successShow.querySelector('.js-change-filters')!.addEventListener('click', async function (e: Event) {
        e.preventDefault();
        successShow.style.display = 'none';
        successHide.style.display = 'block';
    });

    // process form submit: call API
    formElement.addEventListener('submit', async function (e: Event) {
        e.preventDefault();
        await callApi();
        return false;
    });

    // process form submit: call API
    searchInput.addEventListener('keyup', async function (e: Event) {
        filterRows.value = rows.value.filter((r) => r.nombre.toLowerCase().includes(searchInput.value.toLowerCase()));
        resultsCount.innerHTML = filterRows.value.length.toString();
    });
});

async function callApi() {
    let errorMsg = '';

    failurePHolder.style.display = 'none';
    noResultsShow.style.display = 'none';
    successShow.style.display = 'none';

    processingHide.style.display = 'none';
    processingShow.style.display = 'flex';

    if (!errorMsg) {
        // call the API with a generic signature
        try {
            const formObj = formToObject(formElement!);
            const response = await invokeMultipartEncoding(formObj, props.apiUrl!.toString());

            if (response.error) {
                const errors = (<any>response.error).errors ? Object.values((<any>response.error).errors) : [];
                errorMsg =
                    errors.length > 0 && Array.isArray(errors[0]) && errors[0].length
                        ? errors[0][0]
                        : 'Error no identificado. Verifique los datos o reintente más tarde';
            } else if (response.data?.status) {
                errorMsg = response.data.message;
            } else if (!response.data || !(<any>response.data).value) {
                errorMsg = 'No se pudieron obtener los datos - reintente más tarde';
            } else {
                rows.value = (<any>response.data).value.items as ItemInfo[];
                totalCount.value = (<any>response.data).value.totalCount as number;
                reportLink.value = (<any>response.data).value.reportLink as string;
                filterRows.value = rows.value;
            }
        } catch (error) {
            errorMsg = error?.toString() || 'Error interno';
        }
    }

    processingShow.style.display = 'none';
    if (!errorMsg) {
        if (rows.value.length == 0) {
            noResultsShow.style.display = 'block';
        } else {
            // process success
            resultsCount.innerHTML = rows.value.length.toString();
            totalCountElement.innerHTML = totalCount.value.toString();
            if (rows.value.length < totalCount.value) {
                partialResults.style.display = 'block';
                reportLinkElement.href = reportLink.value;
            } else {
                partialResults.style.display = 'none';
            }
            successHide.style.display = 'none';
            successShow.style.display = 'block';
            especLabel.innerHTML = !especTexts?.innerText ? '' : ': ' + especTexts?.innerText;
            zonaLocalLabel.innerHTML = !zonaTexts!.innerText
                ? 'todas las zonas'
                : zonaTexts!.innerText + (!localTexts?.innerText ? '' : ': ' + localTexts?.innerText);
            // try to position map in first item with coordinates
            const first = rows.value.find((r) => r.latitud && r.longitud);
            if (first) {
                // give time for the results to be rendered
                setTimeout(
                    () => showPointInMap(first.latitud, first.longitud, first.nombre),
                    (rows.value.length + 2) / 2,
                );
            } else {
                mapElement.style.display = 'none';
            }
        }
    } else {
        failurePHolder.innerHTML = errorMsg;
        failurePHolder.style.display = 'inline-block';
    }
    processingHide.style.display = 'flex';
}

// event for "see in map" link click
function seeInMapClick(e: Event) {
    e.preventDefault();
    const elem = e.currentTarget as HTMLElement;
    if (elem) {
        const lat = elem.dataset.lat;
        const long = elem.dataset.long;
        const text = elem.dataset.text;
        if (lat && long && text) {
            showPointInMap(lat, long, text);
            const element = mapElement;
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center', // or 'center', 'end', 'nearest'
            });
        }
    }
}

// actual processing to locate a point in the map
async function showPointInMap(lat: string, long: string, text: string) {
    await initMap(parseFloat(lat), parseFloat(long), text);
}

// Load map for specific coordinates
async function initMap(lat: number, long: number, text: string): Promise<void> {
    // The location of Uluru
    const position = { lat: lat, lng: long };

    // Request needed libraries.
    // @ts-ignore
    const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
    // @ts-ignore
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    const message = document.createElement('div');
    message.className = 'marker-style';
    message.textContent = text;

    // The map, centered at Uluru
    map = new Map(document.getElementById('js-map') as HTMLElement, {
        zoom: 16,
        center: position,
        mapId: 'APSOT_MAP_ID',
    });
    // The marker, positioned at Uluru
    const marker = new AdvancedMarkerElement({
        map: map,
        position: position,
        content: message,
    });
    mapElement.style.display = 'block';
}
</script>

<template>
    <div v-if="filterRows.length > 0" class="resultados-listado">
        <div v-for="(item, index) in filterRows" :key="index">
            <div class="box">
                <h3>{{ item.nombre }}</h3>
                <p v-show="item.informacionAdicional" class="info">{{ item.informacionAdicional }}</p>
                <p>{{ item.especialidadNombre }}</p>
                <ul>
                    <li><i class="icon-location"></i> {{ item.direccion }}</li>
                    <li><i class="icon-location" style="visibility: hidden"></i> {{ item.localidadNombre }}</li>
                    <li v-for="(medio, medioIndex) in item.mediosContacto" :key="medioIndex">
                        <a v-if="medio.tipo === 'mail'" :href="'mailto:' + medio.valor" class="textlink"><i
                                class="icon-mail"></i> {{ medio.valor }}</a>
                        <a v-else-if="medio.tipo === 'telefono'" :href="'tel:' + medio.valor" class="textlink"><i
                                class="icon-phone"></i> {{ medio.valor }}</a>
                        <a v-else-if="medio.tipo === 'whatsapp'"
                            :href="'https://wa.me/' + medio.valor.replace(/[^0-9]/g, '')" class="textlink"><i
                                class="icon-whatsapp"></i> {{ medio.valor }}</a>
                        <a v-else-if="medio.tipo === 'web'" :href="medio.valor" class="textlink"> {{ medio.valor }}</a>
                    </li>
                    <!-- <li>
                        <a :href="'tel:' + item.telefono" class="textlink"><i class="icon-phone"></i> {{ item.telefono
                        }}</a>
                    </li> -->
                </ul>
                <div v-if="item.latitud" class="textlink-wrapper">
                    <a @click="seeInMapClick" class="textlink" :data-text="item.nombre" :data-lat="item.latitud"
                        :data-long="item.longitud"><span> Ver en el mapa </span><i
                            class="icon-arrow-right-outlined-lg"></i>
                    </a>
                </div>
            </div>
        </div>
    </div>
    <div v-else>
        <span>No hay resultados</span>
    </div>
</template>
<style scoped lang="scss"></style>
